const DAY_MS = 86_400_000;

export function SyncCaption({ date, now }: { date: Date; now: Date }) {
  const days = Math.max(0, Math.ceil((date.getTime() - now.getTime()) / DAY_MS));
  const when = days === 0 ? "today" : `in ${days} ${days === 1 ? "day" : "days"}`;
  return (
    <p
      data-testid="solar-next-sync"
      className="mt-4 max-w-md border-l-2 border-[#c0501b] pl-4 font-serif text-base leading-relaxed text-[#2b1f14]"
    >
      Next time your clock and the sun agree:{" "}
      <strong className="font-medium">
        {date.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" })}
      </strong>{" "}
      <span className="font-sans text-sm text-[#8a6f52]">({when})</span>
      <span className="mt-1 block font-sans text-xs italic text-[#8a6f52]">
        On that day the equation of time is zero. Only the longitude correction separates your clock from the sun.
      </span>
    </p>
  );
}

type DayState = "next" | "past" | "upcoming";

const CELL_STYLE: Record<DayState, string> = {
  next: "border-[#c0501b] bg-[#c0501b] text-[#f6efe3]",
  past: "border-[#e6dac4] text-[#a68a6c]",
  upcoming: "border-[#d9c9ad] bg-[#fbf6ea] text-[#2b1f14]",
};

function SyncDay({ date, state, index }: { date: Date; state: DayState; index: number }) {
  const note = state === "next" ? "next" : state === "past" ? "passed" : date.toLocaleDateString(undefined, { weekday: "short" });
  return (
    <li data-testid={`solar-sync-day-${index}`} className={`rounded-sm border px-2 pb-2 pt-1 text-center transition-colors ${CELL_STYLE[state]}`}>
      <span className="block font-sans text-[0.65rem] uppercase tracking-[0.25em] opacity-80">
        {date.toLocaleDateString(undefined, { month: "short" })}
      </span>
      <span className={`block font-serif text-2xl tabular-nums ${state === "past" ? "line-through decoration-1" : ""}`}>
        {date.getDate()}
      </span>
      <span className="block font-sans text-[0.6rem] italic opacity-80">{note}</span>
    </li>
  );
}

export function SyncCalendar({ dates, now, next }: { dates: Date[]; now: Date; next: Date | null }) {
  const nextKey = next?.toDateString();
  const stateOf = (d: Date): DayState =>
    d.toDateString() === nextKey ? "next" : d.getTime() < now.getTime() ? "past" : "upcoming";
  return (
    <div className="mt-6 max-w-md">
      <p className="font-sans text-[0.7rem] uppercase tracking-[0.3em] text-[#8a6f52]">
        Clock and sun agree in {now.getFullYear()}
      </p>
      <ol data-testid="solar-sync-calendar" className="mt-3 grid grid-cols-4 gap-2">
        {dates.map((d, i) => (
          <SyncDay key={d.getTime()} date={d} state={stateOf(d)} index={i} />
        ))}
      </ol>
    </div>
  );
}
